import { Box, TextInput } from '@mantine/core'
import { zodResolver } from '@mantine/form'
import { useForm } from '@mantine/form'
import { AxiosError } from 'axios'
import { z } from 'zod'

import { useMutationUpdateUserIdentity } from '@flecto/core/shared'
import { useTranslation } from '@flecto/translations'
import { type CoreUserIdentity } from '@flecto/types'
import { Modal, type ModalProps, useModal } from '@flecto/ui'
import { useNotifications } from '@flecto/ui/@shared/hooks/useNotifications'

export type ModalChangeEmailProps = ModalProps & {
  identity: CoreUserIdentity
}

export function ModalChangeEmail({
  identity
}: ModalChangeEmailProps) {
  const { t } = useTranslation('saas', { keyPrefix: 'profile_account' })
  const { t: tt } = useTranslation('saas', { keyPrefix: 'general' })
  const { closeModal } = useModal()
  const { showNotification } = useNotifications()
  const { mutateAsync: updateIdentityEmail, isLoading: isUpdating } =
    useMutationUpdateUserIdentity()

  const schema = z.object({
    email: z.string().email({
      message: t('modal_change_email.update_identity_email_invalid_email'),
    }),
  })

  const form = useForm({
    initialValues: {
      email: '',
    },
    validate: zodResolver(schema),
  })

  const handleUpdate = async () => {
    try {
      await updateIdentityEmail({
        auth0_id: identity.auth0_id,
        identity: {
          email: form.values.email,
        },
      })

      showNotification({
        message: t('notifications.email_updated'),
        type: 'success',
      })

      closeModal()
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 422) {
          if (
            error.response.data.errors.at(0).field === 'user_identity[email]'
          ) {
            form.setFieldError(
              'email',
              t('modal_change_email.update_identity_email_already_exists')
            )
          }
        }
      } else {
        showNotification({
          message: (error as Error).message,
          type: 'error',
        })
      }
    }
  }

  return (
    <Modal size='md'>
      <Modal.Title>{t('modal_change_email.title')}</Modal.Title>
      <Modal.Content>
        <Box mb='sm'>
          <TextInput
            label={t('modal_change_email.new_email')}
            placeholder={t('modal_change_email.new_email_placeholder')}
            description={t(
              'modal_change_email.update_identity_email_description',
              {
                replace: { email: identity.email },
              }
            )}
            {...form.getInputProps('email')}
          />
        </Box>
      </Modal.Content>
      <Modal.Close />
      <Modal.Cancel />
      <Modal.Submit
        disabled={!form.isValid()}
        loading={isUpdating}
        onClick={handleUpdate}
        preventClose
        label={tt('actions.confirm')}
      />
    </Modal>
  )
}
